import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Save, X, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const FOCUS_AREAS = [
  { value: "serve", labelKey: "training.focusArea.serve" },
  { value: "volley", labelKey: "training.focusArea.volley" },
  { value: "return", labelKey: "training.focusArea.return" },
  { value: "bandeja", labelKey: "training.focusArea.bandeja" },
  { value: "smash", labelKey: "training.focusArea.smash" },
  { value: "wall_play", labelKey: "training.focusArea.wallPlay" },
  { value: "positioning", labelKey: "training.focusArea.positioning" },
  { value: "footwork", labelKey: "training.focusArea.footwork" },
];
const LEVELS = [
  { value: "beginner", labelKey: "training.level.beginner" },
  { value: "intermediate", labelKey: "training.level.intermediate" },
  { value: "advanced", labelKey: "training.level.advanced" },
  { value: "elite", labelKey: "training.level.elite" },
];

interface ExerciseForm {
  name: string;
  description: string;
  focus_areas: string[];
  level: string;
  duration_minutes: number | null;
  min_players: number | null;
  max_players: number | null;
  is_shared: boolean;
}

const emptyForm: ExerciseForm = {
  name: "",
  description: "",
  focus_areas: [],
  level: "beginner",
  duration_minutes: null,
  min_players: null,
  max_players: null,
  is_shared: true,
};

export default function ExerciseLibrary() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterFocus, setFilterFocus] = useState<string>("all");
  const [filterLevel, setFilterLevel] = useState<string>("all");
  const [editing, setEditing] = useState<string | null>(null); // exercise id or "new"
  const [form, setForm] = useState<ExerciseForm>(emptyForm);

  const { data: exercises = [], isLoading } = useQuery({
    queryKey: ["exercises", organization?.id, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("exercises")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("name");
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing === "new") {
        const { error } = await supabase.from("exercises").insert([{
          name: form.name,
          description: form.description || null,
          focus_areas: form.focus_areas,
          level: form.level,
          duration_minutes: form.duration_minutes,
          min_players: form.min_players,
          max_players: form.max_players,
          is_shared: form.is_shared,
          created_by: user!.id,
          organization_id: organization!.id,
        }]);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("exercises")
          .update({
            name: form.name,
            description: form.description || null,
            focus_areas: form.focus_areas,
            level: form.level,
            duration_minutes: form.duration_minutes,
            min_players: form.min_players,
            max_players: form.max_players,
            is_shared: form.is_shared,
          })
          .eq("id", editing!);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercises"] });
      setEditing(null);
      setForm(emptyForm);
      toast({ title: editing === "new" ? t("training.exerciseCreated") : t("training.exerciseUpdated") });
    },
    onError: () => toast({ title: t("training.saveFailed"), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("exercises").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercises"] });
      toast({ title: t("training.exerciseDeleted") });
    },
  });

  const startEdit = (exercise: any) => {
    setEditing(exercise.id);
    setForm({
      name: exercise.name,
      description: exercise.description || "",
      focus_areas: exercise.focus_areas || [],
      level: exercise.level || "beginner",
      duration_minutes: exercise.duration_minutes,
      min_players: exercise.min_players,
      max_players: exercise.max_players,
      is_shared: exercise.is_shared ?? true,
    });
  };

  const toggleFormFocus = (area: string) => {
    setForm((prev) => ({
      ...prev,
      focus_areas: prev.focus_areas.includes(area)
        ? prev.focus_areas.filter((a) => a !== area)
        : [...prev.focus_areas, area],
    }));
  };

  const filtered = exercises.filter((e: any) => {
    if (search && !e.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterFocus !== "all" && !(e.focus_areas || []).includes(filterFocus)) return false;
    if (filterLevel !== "all" && e.level !== filterLevel) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("training.searchExercise")} className="pl-9" />
        </div>
        <Select value={filterFocus} onValueChange={setFilterFocus}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder={t("training.focusAreaFilter")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("training.allAreas")}</SelectItem>
            {FOCUS_AREAS.map((f) => (
              <SelectItem key={f.value} value={f.value}>{t(f.labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterLevel} onValueChange={setFilterLevel}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder={t("training.levelFilter")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("training.allLevels")}</SelectItem>
            {LEVELS.map((l) => (
              <SelectItem key={l.value} value={l.value}>{t(l.labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => { setEditing("new"); setForm(emptyForm); }}>
          <Plus className="h-4 w-4 mr-1" /> {t("training.newExercise")}
        </Button>
      </div>

      {/* Form (inline) */}
      {editing && (
        <Card>
          <CardContent className="py-4 px-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>{t("training.name")} *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>{t("training.levelLabel")}</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{t(l.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("training.description")}</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div>
              <Label className="mb-1.5 block">{t("training.focusAreas")}</Label>
              <div className="flex flex-wrap gap-1.5">
                {FOCUS_AREAS.map((area) => (
                  <Badge
                    key={area.value}
                    variant={form.focus_areas.includes(area.value) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleFormFocus(area.value)}
                  >
                    {t(area.labelKey)}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{t("training.durationMin")}</Label>
                <NumberInput
                  value={form.duration_minutes ?? undefined}
                  onValueChange={(v) => setForm({ ...form, duration_minutes: v || null })}
                />
              </div>
              <div>
                <Label>{t("training.minPlayers")}</Label>
                <NumberInput
                  value={form.min_players ?? undefined}
                  onValueChange={(v) => setForm({ ...form, min_players: v || null })}
                />
              </div>
              <div>
                <Label>{t("training.maxPlayers")}</Label>
                <NumberInput
                  value={form.max_players ?? undefined}
                  onValueChange={(v) => setForm({ ...form, max_players: v || null })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_shared} onCheckedChange={(v) => setForm({ ...form, is_shared: v })} />
              <Label>{t("training.sharedWithCoaches")}</Label>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => saveMutation.mutate()} disabled={!form.name.trim() || saveMutation.isPending}>
                <Save className="h-4 w-4 mr-1" /> {t("training.save")}
              </Button>
              <Button variant="ghost" onClick={() => { setEditing(null); setForm(emptyForm); }}>
                <X className="h-4 w-4 mr-1" /> {t("training.abort")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      {isLoading && <p className="text-muted-foreground text-sm">{t("training.loadingExercises")}</p>}

      <div className="space-y-2">
        {filtered.map((ex: any) => (
          <Card key={ex.id}>
            <CardContent className="py-3 px-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{ex.name}</span>
                    {ex.level && (
                      <Badge variant="secondary" className="text-xs capitalize">
                        {LEVELS.find((l) => l.value === ex.level) ? t(LEVELS.find((l) => l.value === ex.level)!.labelKey) : ex.level}
                      </Badge>
                    )}
                    {ex.duration_minutes && (
                      <span className="text-xs text-muted-foreground">{ex.duration_minutes} min</span>
                    )}
                    {(ex.min_players || ex.max_players) && (
                      <span className="text-xs text-muted-foreground">
                        {ex.min_players || "?"}-{ex.max_players || "?"} {t("training.players")}
                      </span>
                    )}
                    {!ex.is_shared && <Badge variant="outline" className="text-xs">{t("training.private")}</Badge>}
                  </div>
                  {ex.description && <p className="text-xs text-muted-foreground mt-1">{ex.description}</p>}
                  {(ex.focus_areas || []).length > 0 && (
                    <div className="flex gap-1 mt-1.5">
                      {ex.focus_areas.map((f: string) => (
                        <Badge key={f} variant="outline" className="text-xs">
                          {FOCUS_AREAS.find(a => a.value === f) ? t(FOCUS_AREAS.find(a => a.value === f)!.labelKey) : f}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => startEdit(ex)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteMutation.mutate(ex.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
